import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useConsultationNotes,
  useCreateConsultationNote,
} from "@/hooks/api/useConsultationNotes";
import { useUserProfiles } from "@/hooks/api/useAuthorization";
import { getCurrentAuthUserId } from "@/lib/current-user";

function shortDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const date = `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
  const time = `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  return `${date} ${time}`;
}

export function ConsultationNotesList({
  labVisitId,
}: {
  labVisitId: string;
}) {
  const [draft, setDraft] = useState("");
  const { data: notes } = useConsultationNotes(labVisitId);
  const { data: userProfiles } = useUserProfiles();
  const createNote = useCreateConsultationNote();

  const profileByAuthUserId = useMemo(() => {
    const map = new Map<string, { givenName: string | null; familyName: string | null }>();
    for (const p of userProfiles ?? []) {
      map.set(p.authUserId, { givenName: p.givenName, familyName: p.familyName });
    }
    return map;
  }, [userProfiles]);

  const sortedNotes = useMemo(() => {
    // Chat-style: oldest first, newest just above the input row
    return [...(notes ?? [])].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [notes]);

  const authorName = (authorId: string): string => {
    const p = profileByAuthUserId.get(authorId);
    if (!p) return "Ukjent lege";
    const name = [p.givenName, p.familyName].filter(Boolean).join(" ").trim();
    return name || "Ukjent lege";
  };

  const handleSave = async () => {
    if (!draft.trim()) return;
    const authorId = getCurrentAuthUserId();
    if (!authorId) {
      toast.error("Klarte ikke å identifisere innlogget bruker");
      return;
    }
    try {
      await createNote.mutateAsync({
        labVisitId,
        authorId,
        note: draft.trim(),
      });
      setDraft("");
      toast.success("Notat lagret");
    } catch {
      toast.error("Kunne ikke lagre notat");
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Kopiert");
    } catch {
      toast.error("Klarte ikke å kopiere");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-admin-warn-300">
        <FileText className="h-3.5 w-3.5" />
        Notater
      </div>

      <div className="space-y-2">
        {sortedNotes.length === 0 && (
          <p className="text-xs text-admin-text-subtle italic">
            Ingen konsultasjonsnotater enda
          </p>
        )}
        {sortedNotes.map((n) => (
          <div
            key={n.id}
            className="border-l-2 border-l-admin-warn-400/60 bg-admin-warn-500/5 rounded-r p-2"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-admin-text-primary whitespace-pre-wrap leading-relaxed flex-1">
                {n.note}
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 shrink-0"
                onClick={() => handleCopy(n.note)}
                title="Kopier notat"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-[10px] text-admin-text-subtle mt-1">
              {authorName(n.authorId)} · {shortDateTime(n.createdAt)}
            </p>
          </div>
        ))}

        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Legg til konsultasjonsnotat..."
            className="min-h-[140px] text-xs w-full"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={!draft.trim() || createNote.isPending}
              onClick={handleSave}
            >
              Lagre notat
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
